import { useState } from 'react';
import { MailWarning, X } from 'lucide-react';

import { Button } from './ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth/AuthProvider';
import { t } from '../lib/i18n';

type ResendState = 'idle' | 'sending' | 'sent' | 'tooMany' | 'error';

const DISMISS_KEY = 'tb.emailVerifyBanner.dismissed';

/**
 * Banner discreto (tono --sun) para pedirle al usuario que verifique su email.
 *
 * Se muestra solo cuando /auth/me devolvió `email_verified === false`
 * (explícito: si el campo falta, no molestamos). Permite reenviar el correo con
 * feedback y manejo del 429 ("esperá un momento"). Es cerrable por sesión
 * (sessionStorage), no persiste entre sesiones para no ocultarlo para siempre.
 */
export function EmailVerificationBanner() {
  const { user } = useAuth();
  const [resend, setResend] = useState<ResendState>('idle');
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  // Solo si sabemos con certeza que NO está verificado.
  if (!user || user.email_verified !== false || dismissed) return null;

  const handleResend = async () => {
    if (resend === 'sending') return;
    setResend('sending');
    try {
      await api.post('/auth/resend-verification');
      setResend('sent');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResend('tooMany');
      } else {
        setResend('error');
      }
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  // Feedback secundario debajo del texto principal según el resultado.
  let feedback: { text: string; tone: 'ok' | 'warn' | 'danger' } | null = null;
  if (resend === 'sent') feedback = { text: t('verifyEmail.banner.sent'), tone: 'ok' };
  else if (resend === 'tooMany') feedback = { text: t('verifyEmail.banner.tooMany'), tone: 'warn' };
  else if (resend === 'error') feedback = { text: t('verifyEmail.banner.error'), tone: 'danger' };

  const feedbackColor =
    feedback?.tone === 'ok'
      ? 'text-good'
      : feedback?.tone === 'danger'
        ? 'text-danger'
        : 'text-sun';

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-token border border-sun/50 bg-sun/10 px-4 py-3">
      <MailWarning size={18} className="shrink-0 text-sun" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink">{t('verifyEmail.banner.text')}</p>
        {feedback && <p className={`mt-0.5 text-xs ${feedbackColor}`}>{feedback.text}</p>}
      </div>
      <div className="flex items-center gap-1.5">
        {/* Tras un envío exitoso no reofrecemos el botón (evita spam). */}
        {resend !== 'sent' && (
          <Button
            variant="secondary"
            onClick={handleResend}
            loading={resend === 'sending'}
            className="h-8 px-3 text-sm"
          >
            {resend === 'sending'
              ? t('verifyEmail.banner.resending')
              : t('verifyEmail.banner.resend')}
          </Button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('verifyEmail.banner.dismiss')}
          className="grid h-8 w-8 place-items-center rounded-token text-muted transition-colors hover:text-ink"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
